/** 가장자리로부터 이 거리(px) 안에 포인터가 들어오면 스크롤이 돈다. */
export const EDGE_ZONE_PX = 56;

/** 가장자리에 붙었을 때의 최대 속도(px/frame) — 60fps 에서 초당 약 900px. */
export const MAX_SPEED_PX = 15;

/**
 * 드래그 중 포인터 위치가 만드는 **한 프레임의 스크롤 양** — 위로 올릴 때 음수.
 *
 * HTML5 Drag and Drop 은 스크롤 컨테이너를 자동으로 밀어 주지 않는다. 그래서 목록이 화면보다 길면
 * 맨 아래 항목을 잡고도 맨 위로 가져갈 길이 없다 — 끌고 있는 동안에는 휠도 스크롤바도 잡을 수 없기
 * 때문이다(실측: 토공 집계표에서 맨 아래 구역을 맨 위로 못 옮김).
 *
 * 속도는 가장자리에 가까울수록 빠르다. 일정 속도로 밀면 먼 거리는 답답하고 가까운 거리는 지나친다 —
 * 거리에 비례시키면 사용자가 포인터를 조금 움직여 속도를 고를 수 있다.
 *
 * 이미 끝까지 간 방향으로는 **0** 을 돌려준다. 그래야 호출자가 「더 갈 곳이 없다」를 스크롤을 시도해
 * 보지 않고도 알 수 있고, 끝에 닿은 뒤 프레임마다 헛도는 일이 없다.
 */
export function autoScrollDelta({
  pointerY,
  top,
  bottom,
  scrollTop,
  maxScrollTop,
}: {
  pointerY: number;
  /** 스크롤 컨테이너의 뷰포트 기준 위·아래 경계. */
  top: number;
  bottom: number;
  scrollTop: number;
  /** `scrollHeight - clientHeight` — 더 내려갈 수 없는 자리. */
  maxScrollTop: number;
}): number {
  const fromTop = pointerY - top;
  const fromBottom = bottom - pointerY;

  // 벗어난 쪽에도 하한을 두지 않는다 — 목록 위로 포인터를 빼는 것은 「더 위로」라는 뜻이지 멈추라는 뜻이 아니다.
  if (fromTop < EDGE_ZONE_PX) {
    if (scrollTop <= 0) return 0;
    return -speed(EDGE_ZONE_PX - fromTop);
  }
  if (fromBottom < EDGE_ZONE_PX) {
    if (scrollTop >= maxScrollTop) return 0;
    return speed(EDGE_ZONE_PX - fromBottom);
  }
  return 0;
}

/**
 * 가장자리를 얼마나 파고들었는지(px) → 속도. 경계 밖으로 벗어난 만큼도 세므로 컨테이너를 넘어가
 * 끌어도 멈추지 않는다 — 다만 최대 속도를 넘지 않는다.
 */
function speed(depth: number): number {
  const ratio = Math.min(depth / EDGE_ZONE_PX, 1);
  return Math.max(1, Math.round(MAX_SPEED_PX * ratio));
}

/**
 * 세로로 스크롤되는 가장 가까운 조상 — 없으면 `null`.
 *
 * `scrollHeight > clientHeight` 를 함께 보는 이유는 `overflow: auto` 라도 내용이 넘치지 않으면
 * 스크롤할 것이 없어서다. 그런 조상에서 멈추면 정작 스크롤되는 바깥 컨테이너를 놓친다.
 */
export function scrollableAncestor(node: HTMLElement | null): HTMLElement | null {
  for (let current = node; current; current = current.parentElement) {
    const overflowY = getComputedStyle(current).overflowY;
    const scrolls = overflowY === "auto" || overflowY === "scroll" || overflowY === "overlay";
    if (scrolls && current.scrollHeight > current.clientHeight) return current;
  }
  return null;
}
